import React from 'react';
import { StandardSelect } from '@/components/ui/form-fields';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { PenTool, Brush, Palette, BookImage } from 'lucide-react';
import { MoodFilter } from '../filters/MoodFilter';
import { EnvironmentFilter } from '../filters/EnvironmentFilter';
import styles from '../FilterBar.module.css';
import { motion } from 'framer-motion';

interface FilterTabsProps {
  styleOptions: FilterOption[];
  selectedStyle: string | null;
  onStyleChange: (options: FilterOption[]) => void;
  setSelectedStyle: (style: string) => void;
  influenceOptions: FilterOption[];
  selectedInfluence: string | null;
  onInfluenceChange: (options: FilterOption[]) => void;
  setSelectedInfluence: (influence: string) => void;
  colorOptions: FilterOption[];
  onColorChange: (options: FilterOption[]) => void;
  backgroundOptions: FilterOption[];
  onBackgroundChange: (options: FilterOption[]) => void;
  selectedBackground: string | null;
  environmentOptions: FilterOption[];
  onEnvironmentChange: (options: FilterOption[]) => void;
  moodOptions: FilterOption[];
  onMoodChange: (options: FilterOption[]) => void;
  isAdvancedMode: boolean;
}

const FilterTabs: React.FC<FilterTabsProps> = React.memo(({
  styleOptions,
  selectedStyle,
  onStyleChange,
  setSelectedStyle,
  influenceOptions,
  selectedInfluence,
  onInfluenceChange,
  setSelectedInfluence,
  colorOptions,
  onColorChange,
  backgroundOptions,
  onBackgroundChange,
  selectedBackground,
  environmentOptions,
  onEnvironmentChange,
  moodOptions,
  onMoodChange,
  isAdvancedMode
}) => {
  // Animation variants for content sections
  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  // Determine which filters to show based on Simple/Advanced mode
  // Simple mode only shows primary options: Style, Environment, Mood
  // Advanced mode shows all options without restrictions
  const showStyle = true; // Always show
  const showInfluence = isAdvancedMode; // Show in Advanced mode
  const showEnvironment = true; // Always show  
  const showBackground = isAdvancedMode; // Show in Advanced mode
  const showMood = true; // Always show
  const showColors = isAdvancedMode; // Show in Advanced mode

  // Handle style change - no clearing logic needed
  const handleStyleChange = (val: string | string[]) => {
    const value = typeof val === 'string' ? val : '';
    
    setSelectedStyle(value);
    const updatedOptions = styleOptions.map(option => ({
      ...option,
      checked: option.id === value
    }));
    onStyleChange(updatedOptions);
  };

  // Handle influence change - no clearing logic needed
  const handleInfluenceChange = (val: string | string[]) => {
    const value = typeof val === 'string' ? val : '';
    
    setSelectedInfluence(value);
    const updatedOptions = influenceOptions.map(option => ({
      ...option,
      checked: option.id === value
    }));
    onInfluenceChange(updatedOptions);
  };

  return (
    <div className="w-full space-y-6">
      {/* Style & Influence Section */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={contentVariants}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-4">
          {showStyle && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StandardSelect
                id="style"
                label="Art Style"
                icon={<BookImage size={16} />}
                colorVariant="indigo"
                value={selectedStyle || ''}
                onChange={handleStyleChange}
                options={[
                  { value: 'none', label: 'None' },
                  ...styleOptions.map(option => ({ value: option.id, label: option.label }))
                ]}
                placeholder="Style"
                className={styles.vibrantInput}
              />
            </motion.div>
          )}
          
          {showInfluence && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <StandardSelect
                id="influence"
                label="Artistic Influence"
                icon={<Brush size={16} />}
                colorVariant="indigo"
                value={selectedInfluence || ''}
                onChange={handleInfluenceChange}
                options={[
                  { value: 'none', label: 'None' },
                  ...influenceOptions.map(option => ({ value: option.id, label: option.label }))
                ]}
                placeholder="Influence"
                className={styles.vibrantInput}
              />
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Environment Section */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={contentVariants}
        className="space-y-4"
      >
        <EnvironmentFilter 
          environmentOptions={environmentOptions}
          onEnvironmentChange={onEnvironmentChange}
          backgroundOptions={backgroundOptions}
          onBackgroundChange={onBackgroundChange}
          selectedBackground={selectedBackground}
          showEnvironment={showEnvironment}
          showBackground={showBackground}
        />
      </motion.div>

      {/* Mood Section */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={contentVariants}
        className="space-y-4"
      >
        <MoodFilter 
          moodOptions={moodOptions} 
          onMoodChange={onMoodChange} 
          colorOptions={colorOptions}
          onColorChange={onColorChange}
          showMood={showMood}
          showColors={showColors}
        />
      </motion.div>
    </div>
  );
});

FilterTabs.displayName = 'FilterTabs';

export default FilterTabs;
